// Metadata shapes for the usage events, kept pure so they can be tested and so
// the privacy rule is enforceable in one place: these functions may return field
// *names*, counts and lengths, never a value a person typed.
//
// Structurally typed rather than importing the API's own interfaces — telemetry
// is written by shared/api/leads.ts, which would otherwise import a module that
// imports it back.

/** Which fields a contact edit touched. Names only — never the new values. */
export function contactEditMeta(edits: object): { fields: string[] } {
  const values = edits as Record<string, unknown>
  const fields = Object.keys(values)
    .filter(k => values[k] !== undefined)
    .sort()
  return { fields }
}

export interface TopUpLike {
  claimed: unknown[]
  owned: number
  target: number
  openOwned: number
  maxOpen: number
  stopReason: string
}

/**
 * What one top-up did and why it stopped — the answer to "why did I get no
 * leads", which the server already computes and the client used to drop.
 */
export function topUpMeta(res: TopUpLike): Record<string, unknown> {
  return {
    claimed: res.claimed.length,
    stopReason: res.stopReason,
    owned: res.owned,
    target: res.target,
    openOwned: res.openOwned,
    maxOpen: res.maxOpen,
  }
}

/**
 * Whether submitting an outcome wipes a star rating the lead already carried.
 * Outcomes that don't ask for a rating write 0 into the same feedback blob, so
 * a rated lead silently loses its rating — worth counting, not inferring.
 */
export function clearsRating(prev: number | null | undefined, next: number): boolean {
  return (prev ?? 0) > 0 && next === 0
}

/** Length bucket for free text, so nothing typed reaches the event log. */
export function textMeta(body: string): { chars: number } {
  return { chars: body.trim().length }
}

/**
 * Where a tracked action came from. Absent means a click — the common case,
 * so mouse/touch call sites pass nothing and only keyboard handlers build one
 * of these, naming the actual key pressed (e.g. 'R', 'ArrowDown', 'Escape').
 */
export interface KeyTrigger {
  source: 'keyboard'
  key: string
}

/**
 * `source`/`key` metadata for a tracked action — YCRM-20 hid the shortcut
 * hints in the UI, so this is how "is anyone still using them" gets answered
 * before deciding whether to remove them.
 */
export function triggerMeta(via?: KeyTrigger): { source: 'keyboard' | 'click'; key?: string } {
  return via ? { source: 'keyboard', key: via.key } : { source: 'click' }
}

/**
 * The next value for a ref tracking how the outcome sheet is being closed,
 * when a close path reports in. A source-less report (the Cancel button, the
 * X, or Radix's own dismiss firing after ModalShell's onEscapeKeyDown already
 * tagged the same close) must not erase a trigger an earlier, more specific
 * report already set for this close — only a real via overwrites.
 */
export function nextCloseSource(current: KeyTrigger | undefined, via: KeyTrigger | undefined): KeyTrigger | undefined {
  return via ?? current
}

/**
 * Which way a queue-navigation key steps, or null if the key isn't one. → and
 * ↓ (and j) step forward; ← and ↑ (and k) step back — the direction the Focus
 * next/previous buttons' tooltips have always promised.
 */
export function queueNavDirection(key: string): 'forward' | 'back' | null {
  if (key === 'ArrowDown' || key === 'ArrowRight') return 'forward'
  if (key === 'ArrowUp' || key === 'ArrowLeft') return 'back'
  const lower = key.toLowerCase()
  if (lower === 'j') return 'forward'
  if (lower === 'k') return 'back'
  return null
}
